import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Alert, AlertColor, Box, Checkbox, IconButton, Menu, MenuItem, Snackbar, TableCell, TableRow, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LoaderComp from '../../../../../components/Loader/LoaderComp';
import { useProfile } from "../../../../../context/ProfileProvider/useProfile";
import { Group } from "../../../../../model/GroupModel";
import { DeleteGroup } from '../../../../../services/GroupsServices';
import { GetOrganizationGroup, GetOrganizationGroupName } from "../../../../../services/OrganizationServices";
import { DisableUserAndGroupModal } from '../DisableUserAndGroupModal/DisableUserAndGroupModal';
import { GenericTable, IFooterData } from "../GenericTable/GenericTable";
import KeydriveSnackbar from '../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar';

interface IGroupsRow {
    group: Group;
    isGroupTable?: boolean;
    userEditId?:string;
    setUserGroups?: Dispatch<SetStateAction<Group[]>>
    handleSnackbarWithMessage: (message: string, color: AlertColor) => void;
    refreshGroups: Dispatch<SetStateAction<boolean>>;
    nameOrg:string;
}

interface IMenuOptions {
    action: string;
    icon: JSX.Element;
    onClick: () => void;
}

interface IGroupTable {
    isGroupTable: boolean;
    nameOrg: string;
    search?: string;
    userEditId?: string;
    setUserGroups?: Dispatch<SetStateAction<Group[]>>
    setOrganizationGroups?: Dispatch<SetStateAction<Group[]>>
}

const GroupsRow: React.FC<IGroupsRow> = ({nameOrg, group, isGroupTable, userEditId, setUserGroups, handleSnackbarWithMessage, refreshGroups }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [checked, setChecked] = useState<boolean>(false);
    const [isModalDisableOpen, setIsModalDisableOpen] = useState<boolean>(false);
    const profile = useProfile();
    const { t } = useTranslation();

    const userIsGroupMember = group.users?.includes(profile.id!) ?? false;
    const userEditIsGroupMember = group.users?.includes(userEditId!) ?? false;
    const newgroup: Group = group;
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCheck = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setChecked(checked);

        if (checked && setUserGroups) {
            setUserGroups((prevGroups) => [...prevGroups, newgroup]);
        } else if (!checked && setUserGroups) {
            setUserGroups((prevGroups) => prevGroups.filter((g) => g.name !== group.name));
        }
    };
    
    useEffect(() => {
        if (userEditIsGroupMember && setUserGroups) {
            setUserGroups((prevGroups) => {
                if (!prevGroups.some(g => g.id === newgroup.id)) {
                    return [...prevGroups, newgroup]; 
                }
                return prevGroups; 
            });
            setChecked(true);
        }
    }, [userEditIsGroupMember, setUserGroups, newgroup]); 

    const handleDialog = () => {
        setIsModalDisableOpen(!isModalDisableOpen);
    };

    const handleConfirmDelete = () => {
        DeleteGroup(group.id!)
        .then((data) =>{
            handleSnackbarWithMessage(t("GrupoExcluido.message"), 'success')
            refreshGroups(true)
        })
        .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'));
        handleDialog();
    };

    const options: IMenuOptions[] = [
        {
            action: 'Desativar grupo',
            icon: <DeleteIcon />,
            onClick: handleDialog,
        },
    ];

    const handleGroupPage = () => {
        navigate(`/${nameOrg}/adminConsole/usersAndGroups/${group.id}`)
    }

    return (
        <>
        <TableRow>
            <TableCell sx={{ padding: '0px' }}>
                {isGroupTable ? (
                    <>
                        <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls={open ? 'long-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={handleClick}
                        >
                            <MoreVertIcon />
                        </IconButton>
                            <Menu
                                id="long-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'long-button',
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                            >
                                {options.map((option, index) => (
                                    <MenuItem key={index}
                                        sx={{ gap: 1 }}
                                        onClick={() => {
                                        handleClose();
                                        option.onClick();
                                    }}>
                                        {option.icon}
                                        {option.action}
                                    </MenuItem>
                                ))}
                            </Menu>
                    </>
                ) : (
                    <Checkbox 
                        checked={checked} 
                        onChange={handleCheck } 
                    />
                )}
            </TableCell>
            <TableCell sx={{ display: 'flex', alignItems: "center", gap: 1, padding: '8px' }}>
                <Box
                    sx={{
                        height: '26px',
                        width: '26px',
                        color: '#22638D',
                        backgroundColor: '#22638D1A',
                        border: "1px solid #22638D33",
                        borderRadius: '50%',
                        display: 'flex'
                    }}>
                    <GroupIcon sx={{ height: '16px', width: '16px', margin: 'auto' }} />
                </Box>
                    <Typography 
                        sx={{ 
                            "&:hover": {
                            color: '#22638D', 
                            textDecoration: 'underline',
                            cursor: 'pointer'
                        }}}
                        onClick={handleGroupPage}
                    >
                    {group.name}
                </Typography>
            </TableCell>
            <TableCell sx={{ padding: '8px'}}>
                <Typography sx={{ display: 'flex',gap: '4px' }}> 
                        <Typography fontWeight={'bold'}>
                            {userIsGroupMember ? `${t('AdminConsole.MeAnd')} ${group.users ? group.users.length - 1 : 0}` : (group.users ? group.users.length : 0)}
                        </Typography>
                    {t('AdminConsole.UsersParticipates')}
                </Typography>
            </TableCell>
        </TableRow>
            <DisableUserAndGroupModal isopen={isModalDisableOpen} handleClickConfirm={handleConfirmDelete} handleOpenDialog={handleDialog} isUser={false} groupName={group.name} />
        </>
    );
}

export const GroupsTable: React.FC<IGroupTable> = ({ isGroupTable, nameOrg, search, userEditId, setUserGroups, setOrganizationGroups }) => {
    const GROUPS_PER_PAGE = 5;
    const [allGroups, setAllGroups] = useState<Group[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();
    const [refreshGroups, setRefreshGroups] = useState<boolean>(false);
    const [limit] = useState<number>(50);
    const { t } = useTranslation();
    
    const handleSnackbarWithMessage = (message: string, color: AlertColor) => {
        setMessageSnack(message)
        setColorSnack(color)
    }

    useEffect(() => {
        setLoading(true);

        GetOrganizationGroup(nameOrg, 0, limit)
            .then((data) => {
                setAllGroups(data);
                if (setOrganizationGroups) setOrganizationGroups(data);
            })
            .catch(err => handleSnackbarWithMessage(err.response?.data, 'error'))
            .finally(() => setLoading(false));

    }, [nameOrg, refreshGroups]);

    const filteredGroups = useMemo(() => {
        if (!search || search.trim() === '') return allGroups;

        const lower = search.toLowerCase();
        return allGroups.filter(group =>
            group.name?.toLowerCase().includes(lower)
        );
    }, [allGroups, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const displayedGroups = useMemo(
        () => filteredGroups.slice((currentPage - 1) * GROUPS_PER_PAGE, currentPage * GROUPS_PER_PAGE),
        [filteredGroups, currentPage]
    );

    const footerData: IFooterData = {
        dataType: t('AdminConsole.GroupsMin'),
        showingQuantity: displayedGroups.length,
        totalQuantity: filteredGroups.length,
    };

    const headers = [t("AdminConsole.Name"), t("AdminConsole.Participants")];

    return (
        <>
            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LoaderComp />
                </Box>
            ) : (
                <GenericTable
                    footerData={footerData}
                    headers={headers}
                    bodyData={displayedGroups.map((g) => (
                        <GroupsRow nameOrg={nameOrg} refreshGroups={setRefreshGroups} key={g.id} group={g} isGroupTable={isGroupTable} userEditId={userEditId} setUserGroups={setUserGroups} handleSnackbarWithMessage={handleSnackbarWithMessage} />
                    ))}
                    currentPage={currentPage} 
                    onPageChange={setCurrentPage}
                    dataPerPage={GROUPS_PER_PAGE}
                />
            )}

            <KeydriveSnackbar 
                message={messageSnack}
                open={messageSnack !== undefined}
                variant={colorSnack}
                onClose={() => setMessageSnack(undefined)}
            />
        </>
    );
};
